import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from 'common/spinner';
import { Button, ButtonStyle } from 'common/button';
import { roleLabel, ROLE_OPTIONS } from 'common/share-dialog';
import { AppContext } from 'context/app-context';
import { LocalStorageApi } from 'helpers/local-storage';
import { RouteName } from 'routes';
import { history } from 'routes/routes';
import { getRouteFor } from 'routes/route-helpers';
import {
  Invitation as InvitationInfo,
  SharesController,
  shareErrorDetails,
} from 'networking/controllers/shares-controller';
import styles from './invitation.module.scss';

const WHAT: Record<InvitationInfo['resource']['type'], string> = {
  vault: 'the vault',
  album: 'the album',
  memory: 'a memory in',
};

// The page behind every share email (/invite/:token): what was shared, with
// which role and by whom; accepting it signs the person in (or up, with the
// invited email) and takes them straight to what was shared.
export const Invitation = () => {
  const { token } = useParams<{ token: string }>();
  const { state } = useContext(AppContext);
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [wrongAccount, setWrongAccount] = useState(false);
  const [acting, setActing] = useState(false);

  // Back from signing in or up with ?accept=1: accept right away.
  const acceptOnArrival =
    new URLSearchParams(window.location.search).get('accept') === '1';

  useEffect(() => {
    SharesController.getInvitation(token)
      .then(setInfo)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const open = (invitation: InvitationInfo) =>
    history.push(invitation.landingPath);

  const accept = async () => {
    if (!info) return;
    if (!state.logged) {
      // Sign in (or up, as the invited email), then come back to accept.
      LocalStorageApi.set(
        'previous-url',
        `${getRouteFor(RouteName.Invitation, { token })}?accept=1`,
      );
      if (info.hasAccount) {
        history.push(getRouteFor(RouteName.SignIn, {}));
      } else {
        const query = new URLSearchParams({
          email: info.email,
          ...(info.name ? { name: info.name } : {}),
          ...(info.resource.type === 'vault' ? { token } : {}),
        });
        history.push(`${getRouteFor(RouteName.SignUp, {})}?${query}`);
      }
      return;
    }
    setActing(true);
    try {
      open(await SharesController.acceptInvitation(token));
    } catch (error) {
      if (shareErrorDetails(error) === 'wrong_account') setWrongAccount(true);
      else setNotFound(true);
    } finally {
      setActing(false);
    }
  };

  const decline = async () => {
    setActing(true);
    try {
      setInfo(await SharesController.declineInvitation(token));
    } catch {
      setNotFound(true);
    } finally {
      setActing(false);
    }
  };

  useEffect(() => {
    if (acceptOnArrival && state.logged && info?.status === 'invited') {
      accept();
    }
  }, [acceptOnArrival, state.logged, info?.status]);

  if (loading) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    );
  }

  if (notFound || !info) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Invitation not found</h1>
          <p className={styles.text}>
            This invitation link is no longer valid. Ask the person who sent it
            to invite you again.
          </p>
        </div>
      </div>
    );
  }

  const target =
    info.resource.type === 'vault'
      ? info.vaultName
      : info.resource.name || 'Untitled';
  const where =
    info.resource.type === 'memory' ? ` (in ${info.vaultName})` : '';
  const roleOn = info.resource.type;
  const description = ROLE_OPTIONS[roleOn]?.find(
    (option) => option.role === info.role,
  )?.description;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {info.status === 'active' && (
          <>
            <h1 className={styles.title}>You&apos;re in</h1>
            <p className={styles.text}>
              You have access to <strong>{target}</strong> as{' '}
              {roleLabel(info.role, roleOn).toLowerCase()}.
            </p>
            <Button
              buttonStyle={ButtonStyle.Primary}
              onClick={() => open(info)}
            >
              Open
            </Button>
          </>
        )}

        {['declined', 'removed', 'expired'].includes(info.status) && (
          <>
            <h1 className={styles.title}>
              {info.status === 'declined'
                ? 'Invitation declined'
                : 'Invitation no longer valid'}
            </h1>
            <p className={styles.text}>
              {info.status === 'declined'
                ? `You've declined the invitation to ${target}.`
                : 'Ask the person who sent it to invite you again.'}
            </p>
          </>
        )}

        {info.status === 'invited' && (
          <>
            <h1 className={styles.title}>
              {info.inviterName || 'Someone'} invited you
            </h1>
            <p className={styles.text}>
              to {WHAT[info.resource.type]} <strong>{target}</strong>
              {where} on Generational Story, as{' '}
              <strong>{roleLabel(info.role, roleOn).toLowerCase()}</strong>.
            </p>
            {description && <p className={styles.hint}>{description}</p>}
            {wrongAccount && (
              <p className={styles.warning}>
                This invitation is for {info.email}. Sign out and sign in with
                that email to accept it.
              </p>
            )}
            <div className={styles.actions}>
              <Button
                buttonStyle={ButtonStyle.SecondaryGray}
                disabled={acting}
                onClick={decline}
              >
                Decline
              </Button>
              <Button
                buttonStyle={ButtonStyle.Primary}
                disabled={acting}
                onClick={accept}
              >
                {state.logged || !info.hasAccount
                  ? 'Accept'
                  : 'Sign in to accept'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
